import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocale } from '@adsk/alloy-react-locale';
import { InviteMemberModal } from '@adsk/acs-admin-ui-components-shared-components';
import { sortRoles, filterActiveRoles } from './utils';
import AccessLevel from './layout/AccessLevel';
import ProductsAccesses from './layout/ProductsAccesses';
import useTranslateDefaultOptions from './hooks/useTranslateDefaultOptions';

const InviteProjectMemberModal = ({
  companies,
  disableInvite,
  isEmsAccount,
  isLoadingProducts,
  products,
  roles,
  showProducts,
  showProductsSectionRedesign,
  ...rest
}) => {
  const { T, locale: localeName } = useLocale(({ locale }) =>
    require(`./i18n/${locale}.json`)
  );
  const sortedRoles = useMemo(
    () => sortRoles(filterActiveRoles(roles), localeName),
    [roles, localeName]
  );

  const { DEFAULT_OPTION_COMPANIES, DEFAULT_OPTION_ROLES } =
    useTranslateDefaultOptions({ isEmsAccount });

  const accountCompaniesProjectVariant = useMemo(() => {
    return [DEFAULT_OPTION_COMPANIES, ...companies];
  }, [companies, DEFAULT_OPTION_COMPANIES]);

  const accountRolesProjectVariant = useMemo(() => {
    return [DEFAULT_OPTION_ROLES, ...sortedRoles];
  }, [sortedRoles, DEFAULT_OPTION_ROLES]);

  const cancelInviteConfirmationProps = {
    cancelButtonLabel: T.translate('CANCEL_CONFIRMATION_BACK_BUTTON'),
    confirmationButtonLabel: T.translate('CANCEL_CONFIRMATION_CANCEL_BUTTON'),
    message: T.translate('CANCEL_CONFIRMATION_MESSAGE'),
    title: T.translate('CANCEL_CONFIRMATION_TITLE'),
  };

  return (
    <InviteMemberModal
      {...rest}
      companies={accountCompaniesProjectVariant}
      cancelInviteConfirmationProps={cancelInviteConfirmationProps}
      data-testId="InviteProjectMemberModal_Component"
      disableInvite={
        (disableInvite || (!isLoadingProducts && !products.length)) &&
        !isEmsAccount
      }
      isEmsAccount={isEmsAccount}
      modalConfirmButtonText={
        showProductsSectionRedesign
          ? T.translate('FOOTER_CONFIRM_BUTTON')
          : T.translate('FOOTER_CONFIRM_BUTTON_OLD')
      }
      modalTitle={
        showProductsSectionRedesign
          ? T.translate('TITLE')
          : T.translate('TITLE_OLD')
      }
      roles={accountRolesProjectVariant}
      showProductsSectionRedesign={showProductsSectionRedesign}
    >
      <AccessLevel />
      {showProducts && !isEmsAccount && (
        <ProductsAccesses
          isLoadingProducts={isLoadingProducts}
          products={products}
          showProductsSectionRedesign={showProductsSectionRedesign}
        />
      )}
    </InviteMemberModal>
  );
};

InviteProjectMemberModal.propTypes = {
  companies: PropTypes.arrayOf(PropTypes.object).isRequired,
  disableInvite: PropTypes.bool.isRequired,
  isEmsAccount: PropTypes.bool,
  isLoadingProducts: PropTypes.bool.isRequired,
  roles: PropTypes.arrayOf(PropTypes.object).isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      included: PropTypes.bool.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      userAssignable: PropTypes.bool.isRequired,
    })
  ).isRequired,
  showProducts: PropTypes.bool.isRequired,
  showProductsSectionRedesign: PropTypes.bool.isRequired,
};

InviteProjectMemberModal.defaultProps = {
  isEmsAccount: false,
};

export default InviteProjectMemberModal;
